import React from 'react';
import { ClickAwayListener } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectors } from '../../../../reducer';
import * as Styled from './SuggestionStatus.styles';
import {
  PaymentResponseItemCache,
  PaymentType,
  SoftType,
  UserRole,
} from '../../../../../OrdersPage/types';
import { StatusType } from '../../../../../../global/types';
import { PharmacyTag } from '../../../../../SourcePage/types';

interface SuggestionStatusProps {
  onClick: (status: string, statusName: string) => void;
  onClose: () => void;
  next: string[];
  soft: string;
  data: {
    status: string;
    soft: string;
    sourceTags: PharmacyTag[] | null;
    paymentType: string;
    payments: {
      order: PaymentResponseItemCache[];
      items: PaymentResponseItemCache[];
      delivery: PaymentResponseItemCache[];
    };
  };
  role: string;
  unusedStatuses: string[];
}

const SuggestionStatus = ({
  onClick,
  onClose,
  next,
  soft,
  data,
  role,
  unusedStatuses,
}: SuggestionStatusProps) => {
  const allStatuses = useSelector(selectors.allStatuses)
    .filter((el: any) => el.code !== StatusType.CANCELED)
    .filter((el: any) =>
      soft === SoftType.STANDART ? el.code !== 'in_pharmacy_issued' : el.code
    )
    .filter((el: any) =>
      ['interpay', 'mb'].includes(data.paymentType) &&
      (role === UserRole.OPERATOR || role === UserRole.DISPATCHER_B2B)
        ? el.code !== 'waiting_for_payment'
        : el.code
    )
    .filter((el: any) =>
      ['mb'].includes(data.paymentType) &&
      !Object.values(data.payments).some((el) =>
        el.some((item) => ['paid', 'wait_capture'].includes(item.status))
      ) &&
      (role === UserRole.OPERATOR || role === UserRole.DISPATCHER_B2B)
        ? !['request_to_place', 'in_pharmacy_placed'].includes(el.code)
        : el.code
    )
    .filter((el: any) =>
      data.paymentType === PaymentType.EMDEL && role === UserRole.DISPATCHER_B2B
        ? ![
            'waiting_for_payment',
            'in_delivery',
            'delivered',
            'waiting_receipt',
            'generate_invoice',
            'waiting_for_delivery',
            'autodelivery',
            'delivery_claim_created',
            'calculate_delivery',
          ].includes(el.code)
        : el.code
    );

  const excludeNext = next
    .filter((el: any) =>
      ['mb'].includes(data.paymentType) &&
      !Object.values(data.payments).some((el) =>
        el.some((item) => ['paid', 'wait_capture'].includes(item.status))
      ) &&
      (role === UserRole.OPERATOR || role === UserRole.DISPATCHER_B2B)
        ? !['request_to_place', 'in_pharmacy_placed'].includes(el)
        : el
    )
    .filter((el: any) =>
      ['interpay', 'mb'].includes(data.paymentType) &&
      (role === UserRole.OPERATOR || role === UserRole.DISPATCHER_B2B)
        ? el !== 'waiting_for_payment'
        : el
    )
    .filter((el: any) =>
      data.paymentType === PaymentType.EMDEL && role === UserRole.DISPATCHER_B2B
        ? ![
            'waiting_for_payment',
            'in_delivery',
            'delivered',
            'waiting_receipt',
            'generate_invoice',
            'waiting_for_delivery',
            'autodelivery',
            'delivery_claim_created',
            'calculate_delivery',
          ].includes(el.code)
        : el.code
    );

  const all = new Set(
    allStatuses
      .map((el: any) => el.code)
      .filter((el: any) => !unusedStatuses.includes(el))
  );

  const infoAptekaStatuses = new Set(
    allStatuses
      .map((el: any) => el.code)
      .filter(
        (el: any) =>
          el !== 'in_pharmacy_collecting' &&
          el !== 'in_pharmacy_issued' &&
          el !== 'in_pharmacy_collected' &&
          el !== 'completed'
      )
  );

  const hasTag1044 = data.sourceTags?.some((tag) => tag.id === 1044);

  const statusName = (code: string) => {
    return allStatuses.find((el: any) => el.code === code)?.name;
  };

  const isStandartAndSelectStatus =
    data.soft === SoftType.STANDART
      ? data.status !== StatusType.IN_DELIVERY &&
        data.status !== StatusType.WAITING_FOR_DELIVERY &&
        data.status !== StatusType.IN_PHARMACY_READY &&
        data.status !== StatusType.IN_PHARMACY_ISSUED &&
        data.status !== StatusType.DELIVERED
      : true;

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Styled.SuggestionContainer>
        {excludeNext
          ?.filter((el: any) => !unusedStatuses.includes(el))
          ?.map((item: any) => (
            <Styled.StatusNext
              onClick={() => onClick(item, statusName(item))}
              key={item}
            >
              {statusName(item)}
            </Styled.StatusNext>
          ))}
        {excludeNext.length !== 0 && <div className="border" />}
        {hasTag1044
          ? [...infoAptekaStatuses]?.map((item: any) => (
              <Styled.Status
                onClick={() => onClick(item, statusName(item))}
                key={item}
              >
                {statusName(item)}
              </Styled.Status>
            ))
          : [...all]?.map((item: any) => (
              <Styled.Status
                onClick={() => onClick(item, statusName(item))}
                key={item}
              >
                {statusName(item)}
              </Styled.Status>
            ))}
      </Styled.SuggestionContainer>
    </ClickAwayListener>
  );
};

export default SuggestionStatus;
